import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import {cloneDeep, orderBy, shuffle} from 'lodash';
import moment from 'moment';
import axios from 'axios';
import Select from 'react-select';
import {toSystemDateTime} from '../utils/date';
import StatisticsHeadingCounts from './StatisticsHeadingCounts';
import CompetitionsByDepartment from './CompetitionsByDepartment';
import PieChartWithTable from './PieChartWithTable';
import ErrorMessageContainer from '../ErrorMessageContainer';
import ContractsDashboardComponent from './ContractsDashboardComponent';
import ExcelExport from './ExcelExport';

const generateRandomColors = (amount) => {
    const saturation = 100;
    const lightness = 50;
    const alpha = 1.0;

    const colors = [];
    const huedelta = Math.trunc(360 / amount);

    for (let i = 0; i < amount; i++) {
        const hue = i * huedelta;
        colors.push(`hsla(${hue},${saturation}%,${lightness}%,${alpha})`);
    }

    return shuffle(colors);
}

const parsePieChartData = (responseData, colorsOverride = null) => {
    const results = orderBy(responseData, ['cnt'], ['desc']);
    const ids = results.map(({value}) => value);
    const labels = results.map(({label}) => label);
    const data = results.map(({cnt}) => cnt);

    let colors;

    if (colorsOverride) {
        colors = ids.map((value) => colorsOverride[value]);
    } else {
        colors = generateRandomColors(data.length);
    }

    return {
        ids,
        labels,
        datasets: [
            {
                label: '',
                data,
                backgroundColor: colors,
            },
        ],
    };
};

const parseResponses = (responses, hasIContracts = false) => {
    let data = {
        counts: responses[0].data,
        competitionsByDepartment: responses[1].data,
        competitionsByType: parsePieChartData(responses[2].data, {
            'default': '#57A773',
            '1stage': '#CFF3FF',
            '2stages': '#CD8B76',
            '3stages': '#C45BAA',
        }),
        competitionsByNegotiation: parsePieChartData(responses[3].data, {
            'yes': '#1ABB9C',
            'no': '#d9534f',
        }),
        competitionRoundsImprovement: parsePieChartData(responses[4].data, {
            'yes': '#1ABB9C',
            'no': '#d9534f',
        }),
    };

    if (hasIContracts) {
        data = {
            ...data,
            totalNumberOfContracts: responses[5].data,
            numberOfContracts: parsePieChartData(responses[6].data),
            totalContractAmount: responses[7].data,
            contractAmounts: parsePieChartData(responses[8].data),
            tendersVsDaNumber: parsePieChartData(responses[9].data, {
                'tender': '#57A773',
                'da': '#CFF3FF',
                'amendment': '#CD8B76',
                'empty': '#C45BAA',
            }),
            tendersVsDaValue: parsePieChartData(responses[10].data, {
                'tender': '#57A773',
                'da': '#CFF3FF',
                'amendment': '#CD8B76',
                'empty': '#C45BAA',
            }),
            topSuppliersContractValue: parsePieChartData(responses[11].data),
            savingsAmountByYear: parsePieChartData(responses[12].data),
            savingsAmountByDepartment: parsePieChartData(responses[13].data),
            savingsAmountByRfqProcess: parsePieChartData(responses[14].data),
            competitionCompletionTimeByRfqProcess: parsePieChartData(responses[15].data),
            contractCreationTimeByYear: parsePieChartData(responses[16].data),
            contractCreationTimeByDepartment: parsePieChartData(responses[17].data),
            top10Suppliers: responses[18].data,
        };
    }

    return data;
}

export default class DashboardComponent extends Component {
    constructor(props) {
        super(props);

        const dateTo = moment();
        const dateFrom = dateTo.clone().subtract(1, 'year');

        const {
            accountHasNegotiation,
            accountEnabledCompetitionTypes,
            departments,
            hasIContracts,
            rfqProcesses,
        } = window;

        this.state = {
            loading: false,
            errors: [],
            minDate: dateFrom.clone(),
            maxDate: dateTo.clone(),
            params: {
                dateFrom: dateFrom.clone(),
                dateTo: dateTo.clone(),
                department: null,
                rfqProcess: null,
            },
            data: {
                counts: {},
                top10Suppliers: [],
                competitionsByDepartment: [],
                competitionsByType: [],
                competitionsByNegotiation: [],
                competitionRoundsImprovement: [],
                totalNumberOfContracts: {},
                numberOfContracts: [],
                totalContractAmount: {},
                contractAmounts: [],
                tendersVsDaNumber: [],
                tendersVsDaValue: [],
                topSuppliersContractValue: [],
                savingsAmountByYear: [],
                savingsAmountByDepartment: [],
                savingsAmountByRfqProcess: [],
                competitionCompletionTimeByRfqProcess: [],
                contractCreationTimeByYear: [],
                contractCreationTimeByDepartment: [],
            },
        };

        this.accountHasNegotiation = accountHasNegotiation;
        this.accountEnabledCompetitionTypes = accountEnabledCompetitionTypes;
        this.departments = departments;
        this.hasIContracts = hasIContracts;
        this.rfqProcesses = rfqProcesses;

        this.fetchData = this.fetchData.bind(this);
        this.onParamChange = this.onParamChange.bind(this);
        this.onSearchButtonClick = this.onSearchButtonClick.bind(this);
    }

    componentDidMount() {
        this.fetchData();
    }

    fetchData() {
        let {dateFrom, dateTo, department, rfqProcess} = this.state.params;

        dateFrom = dateFrom
            ? toSystemDateTime(dateFrom.clone().startOf('day'))
            : null;

        dateTo = dateTo
            ? toSystemDateTime(dateTo.clone().endOf('day'))
            : null;

        const params = {
            date_from: dateFrom,
            date_to: dateTo,
            department_id: department ? department.value : null,
            rfq_process: rfqProcess ? rfqProcess.value : null,
        };

        let promises = [
            axios.get('/dashboard/counts', {params}),
            axios.get('/dashboard/competitions-by-department', {params}),
            axios.get('/dashboard/competitions-by-type', {params}),
            axios.get('/dashboard/competitions-by-negotiation', {params}),
            axios.get('/dashboard/competition-rounds-improvement', {params}),
        ];

        if (this.hasIContracts) {
            promises = [
                ...promises,
                axios.get('/dashboard/contracts/total-number-of-contracts', {params}),
                axios.get('/dashboard/contracts/number-of-contracts', {params}),
                axios.get('/dashboard/contracts/total-contract-amount', {params}),
                axios.get('/dashboard/contracts/contract-amounts', {params}),
                axios.get('/dashboard/contracts/tenders-vs-da-number', {params}),
                axios.get('/dashboard/contracts/tenders-vs-da-value', {params}),
                axios.get('/dashboard/contracts/top-suppliers-contract-value', {params}),
                axios.get('/dashboard/contracts/savings-amount-by-year', {params}),
                axios.get('/dashboard/contracts/savings-amount-by-department', {params}),
                axios.get('/dashboard/contracts/savings-amount-by-rfq-process', {params}),
                axios.get('/dashboard/contracts/competition-completion-time-by-rfq-process', {params}),
                axios.get('/dashboard/contracts/contract-creation-time-by-year', {params}),
                axios.get('/dashboard/contracts/contract-creation-time-by-department', {params}),
                axios.get('/dashboard/contracts/top-10-suppliers', {params}),
            ];
        }

        this.setState({loading: true, errors: []}, () => {
            axios
                .all(promises)
                .then((responses) => {
                    this.setState({
                        loading: false,
                        data: parseResponses(responses, this.hasIContracts),
                    });
                })
                .catch((error) => {
                    this.setState({
                        loading: false,
                        errors: error.response.data,
                    });
                });
        });
    }

    onParamChange(key, value) {
        const newParams = cloneDeep(this.state.params);

        newParams[key] = value;

        const newState = {params: newParams};

        if (key === 'dateFrom') {
            newState.maxDate = value
                ? value.clone().add(1, 'year')
                : null;
        } else if (key === 'dateTo') {
            newState.minDate = value
                ? value.clone().subtract(1, 'year')
                : null;
        }

        this.setState(newState);
    }

    onSearchButtonClick(e) {
        e.preventDefault();
        this.fetchData();
    }

    render() {
        return (
            <div>
                <ErrorMessageContainer errors={this.state.errors}/>

                <div className="row">
                    <div className="col-md-12">
                        <form className="form-inline">
                            <div className="form-group">
                                <DatePicker className="form-control"
                                            name="date_from"
                                            id="date_from"
                                            selected={this.state.params.dateFrom}
                                            onChange={(value) => this.onParamChange('dateFrom', value)}
                                            dateFormat="DD/MM/YYYY"
                                            locale="el"
                                            placeholderText={window.__js_dashboard.date_from}
                                            minDate={this.state.minDate}/>
                            </div>
                            <div className="form-group">
                                <DatePicker className="form-control"
                                            name="date_to"
                                            id="date_to"
                                            selected={this.state.params.dateTo}
                                            onChange={(value) => this.onParamChange('dateTo', value)}
                                            dateFormat="DD/MM/YYYY"
                                            locale="el"
                                            placeholderText={window.__js_dashboard.date_to}
                                            maxDate={this.state.maxDate}/>
                            </div>
                            <div className="form-group" style={{minWidth: '250px'}}>
                                <Select name="department_id"
                                        id="department_id"
                                        value={this.state.params.department}
                                        options={this.departments}
                                        onChange={(value) => this.onParamChange('department', value)}
                                        clearable={true}
                                        placeholder={window.__js_general.all_departments}/>
                            </div>
                            {
                                this.hasIContracts
                                    ? <div className="form-group" style={{minWidth: '250px'}}>
                                        <Select name="rfq_process"
                                                id="rfq_process"
                                                value={this.state.params.rfqProcess}
                                                options={this.rfqProcesses}
                                                onChange={(value) => this.onParamChange('rfqProcess', value)}
                                                clearable={true}
                                                placeholder={window.__js_general.all_rfq_processes}/>
                                    </div>
                                    : null
                            }
                            <button className="btn btn-primary"
                                    onClick={this.onSearchButtonClick}
                                    disabled={this.state.loading}>
                                <i className="fa fa-fw fa-search"></i>
                                {window.__js_dashboard.search_literal}
                            </button>
                            {
                                this.state.loading
                                    ? null
                                    : <ExcelExport
                                        params={this.state.params}
                                        data={this.state.data}
                                        style={{marginLeft: '10px'}}
                                        hasIContracts={this.hasIContracts}
                                        accountEnabledCompetitionTypes={this.accountEnabledCompetitionTypes}
                                        accountHasNegotiation={this.accountHasNegotiation}
                                    />
                            }
                            {
                                this.state.loading
                                    ? <div className="form-group" style={{marginLeft: '10px'}}>
                                        <span>
                                            <i className="fa fa-fw fa-spin fa-spinner"></i> {window.__js_general.please_wait}
                                        </span>
                                    </div>
                                    : null
                            }
                        </form>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <StatisticsHeadingCounts
                            data={this.state.data.counts}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <CompetitionsByDepartment
                            data={this.state.data.competitionsByDepartment}
                        />
                    </div>
                </div>

                {
                    this.accountEnabledCompetitionTypes.length > 1
                        ? <div className="row">
                            <div className="col-md-12">
                                <PieChartWithTable
                                    title={window.__js_dashboard.competitions_by_type}
                                    data={this.state.data.competitionsByType}
                                    hasIntegerValues={true}
                                />
                            </div>
                        </div>
                        : null
                }

                {
                    this.accountHasNegotiation
                        ? <div className="row">
                            <div className="col-md-12">
                                <PieChartWithTable
                                    title={window.__js_dashboard.competitions_with_negotiation}
                                    data={this.state.data.competitionsByNegotiation}
                                    hasIntegerValues={true}
                                />
                            </div>
                        </div>
                        : null
                }

                {
                    this.hasIContracts
                        ? <ContractsDashboardComponent
                            top10Suppliers={this.state.data.top10Suppliers}
                            totalNumberOfContracts={this.state.data.totalNumberOfContracts}
                            numberOfContracts={this.state.data.numberOfContracts}
                            totalContractAmount={this.state.data.totalContractAmount}
                            contractAmounts={this.state.data.contractAmounts}
                            tendersVsDaNumber={this.state.data.tendersVsDaNumber}
                            tendersVsDaValue={this.state.data.tendersVsDaValue}
                            topSuppliersContractValue={this.state.data.topSuppliersContractValue}
                            savingsAmountByYear={this.state.data.savingsAmountByYear}
                            savingsAmountByDepartment={this.state.data.savingsAmountByDepartment}
                            savingsAmountByRfqProcess={this.state.data.savingsAmountByRfqProcess}
                            competitionCompletionTimeByRfqProcess={this.state.data.competitionCompletionTimeByRfqProcess}
                            contractCreationTimeByYear={this.state.data.contractCreationTimeByYear}
                            contractCreationTimeByDepartment={this.state.data.contractCreationTimeByDepartment}
                        />
                        : null
                }

                <div className="row">
                    <div className="col-md-12">
                        <PieChartWithTable
                            title={window.__js_dashboard.competition_rounds_improvement}
                            data={this.state.data.competitionRoundsImprovement}
                            hasIntegerValues={true}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const element = document.getElementById('dashboard-component');

if (element) {
    ReactDOM.render(<DashboardComponent/>, element);
}